'use client'

import { useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'

/**
 * Barra de progresso fina no topo da página.
 * Inicia imediatamente ao clicar num link interno,
 * completa quando o pathname muda (navegação concluída).
 */
export function NavigationProgress() {
  const pathname = usePathname()
  const barRef = useRef<HTMLDivElement>(null)
  const glowRef = useRef<HTMLDivElement>(null)
  const completingRef = useRef(false)
  const rafRef = useRef<number>(0)

  // Inicia a barra ao detectar clique num link interno
  useEffect(() => {
    const start = () => {
      const bar = barRef.current
      const glow = glowRef.current
      if (!bar || !glow || completingRef.current) return

      // Reset imediato sem transição
      bar.style.transition = 'none'
      bar.style.opacity = '1'
      bar.style.width = '0%'
      glow.style.opacity = '1'

      // Força reflow para a transição funcionar
      void bar.offsetWidth

      // Avança até 80% de forma aparentemente progressiva
      bar.style.transition = 'width 8s cubic-bezier(0.08, 0.02, 0.01, 1)'
      bar.style.width = '80%'
    }

    const handleMouseDown = (e: MouseEvent) => {
      const anchor = (e.target as Element).closest('a')
      if (!anchor) return

      const href = anchor.getAttribute('href')
      if (!href) return

      // Ignora links externos, âncoras e o pathname actual
      if (
        href.startsWith('http') ||
        href.startsWith('//') ||
        href.startsWith('#') ||
        href.startsWith('mailto:') ||
        href.startsWith('tel:') ||
        href === pathname
      ) return

      completingRef.current = false
      start()
    }

    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [pathname])

  // Completa a barra quando o pathname muda (navegação concluída)
  useEffect(() => {
    const bar = barRef.current
    const glow = glowRef.current
    if (!bar || !glow) return

    completingRef.current = true

    // Completa rapidamente para 100%
    bar.style.transition = 'width 0.15s ease-out'
    bar.style.width = '100%'

    // Depois fade out suave
    const fadeTimer = setTimeout(() => {
      bar.style.transition = 'opacity 0.25s ease-out'
      glow.style.transition = 'opacity 0.25s ease-out'
      bar.style.opacity = '0'
      glow.style.opacity = '0'

      // Reset para próxima navegação
      const resetTimer = setTimeout(() => {
        bar.style.transition = 'none'
        bar.style.width = '0%'
        completingRef.current = false
      }, 250)

      return () => clearTimeout(resetTimer)
    }, 150)

    return () => {
      clearTimeout(fadeTimer)
      cancelAnimationFrame(rafRef.current)
    }
  }, [pathname])

  return (
    <>
      {/* Barra principal */}
      <div
        ref={barRef}
        aria-hidden="true"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          height: '2px',
          width: '0%',
          opacity: 0,
          zIndex: 9999,
          background: 'linear-gradient(90deg, #F06823, #FF843D)',
          borderRadius: '0 2px 2px 0',
          pointerEvents: 'none',
          willChange: 'width, opacity',
        }}
      />
      {/* Glow suave */}
      <div
        ref={glowRef}
        aria-hidden="true"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          opacity: 0,
          zIndex: 9998,
          background: 'rgba(240, 104, 35, 0.15)',
          pointerEvents: 'none',
        }}
      />
    </>
  )
}
